import {
  useCallback, useEffect, useRef, useState,
} from 'react';

export const IS_FULLY_SHOWN_THRESHOLD_OR_MARGIN = 1.0;
export const IS_SINGLE_PX_SHOWN_THRESHOLD_OR_MARGIN = 0.0;

export const DOCUMENT_ROOT_NODE = null;

const defaultOptions = {
  threshold: IS_FULLY_SHOWN_THRESHOLD_OR_MARGIN,
  root: DOCUMENT_ROOT_NODE,
};

/**
 * Determine if an element is being shown (by seeing if it intersects a root element, null is `<html>`).
 *
 * This will be invoked on every instance of the element coming in to view.
 *
 * @param callback The callback to be invoked when an item is shown/intersects.
 * @param options The options supplied to the intersection observer
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */
const useIntersectionObserver = (
  // eslint-disable-next-line no-unused-vars
  callback: (ioEnt: IntersectionObserverEntry) => void,
  options: IntersectionObserverInit = defaultOptions,
) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) { return; }
    const refCurrent = ref.current;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          callback(entry);
        }
      });
    }, options);

    if (ref.current) {
      observer.observe(refCurrent);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (refCurrent) {
        observer.unobserve(refCurrent);
      }
    };
  }, [callback, options]);

  return ref;
};

/**
 * Determine if an element is being shown (by seeing if it intersects a root element, null is `<html>`).
 *
 * This version only calls the callback once for each element instance.
 *
 * @param callback The callback to be invoked when an item is shown/intersects.
 * @param options The options supplied to the intersection observer
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
 */
function useSingleCallIntersectionObserver(
  // eslint-disable-next-line no-unused-vars
  callback: (entry: IntersectionObserverEntry) => void,
  options: IntersectionObserverInit = defaultOptions,
) {
  const [hasBeenShown, setHasBeenShown] = useState(false);

  const handleVisibilityChange = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!hasBeenShown) {
        callback(entry);
        setHasBeenShown(true);
      }
    },
    [callback, hasBeenShown],
  );

  return useIntersectionObserver(handleVisibilityChange, options);
}

export default useSingleCallIntersectionObserver;
export const useIsShowing = useIntersectionObserver;
